import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  useReactTable,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  flexRender,
} from "@tanstack/react-table";
import { rankItem } from "@tanstack/match-sorter-utils";
import { All, Search, notFound } from "../../Constant";
import { RiSearchLine, RiArrowUpSLine, RiArrowDownSLine } from "react-icons/ri";
import { X as ClearIcon } from "react-feather";

const DebouncedInput = ({ value: initialValue, onChange, debounce = 500, ...props }) => {
  const [value, setValue] = useState(initialValue);
  const [uid] = useState(() => `dbi-${Math.random().toString(36).slice(2, 8)}`);
  useEffect(() => {
    setValue(initialValue);
  }, [initialValue]);
  useEffect(() => {
    const t = setTimeout(() => onChange(value), debounce);
    return () => clearTimeout(t);
  }, [debounce, onChange, value]);
  return (
    <div style={{ position: "relative" }}>
      <style>{`
        .${uid} { padding-left: 32px !important; padding-right: ${value ? "30px" : "12px"} !important; }
      `}</style>
      <RiSearchLine
        style={{
          position: "absolute",
          left: 10,
          top: "50%",
          transform: "translateY(-50%)",
          color: "var(--text-muted)",
          fontSize: 14,
          pointerEvents: "none",
        }}
      />
      <input
        {...props}
        value={value}
        id="search-bar-0"
        className={`form-control search ${uid}`}
        onChange={(e) => setValue(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Enter") e.preventDefault();
        }}
      />
      {value && (
        <button
          type="button"
          onClick={() => {
            setValue("");
            onChange("");
          }}
          aria-label="Clear search"
          style={{
            position: "absolute",
            right: 8,
            top: "50%",
            transform: "translateY(-50%)",
            background: "rgba(100,116,139,0.1)",
            border: "none",
            padding: 3,
            cursor: "pointer",
            color: "var(--text-muted)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            lineHeight: 1,
            borderRadius: 4,
            zIndex: 2,
          }}
        >
          <ClearIcon size={12} strokeWidth={2.5} />
        </button>
      )}
    </div>
  );
};

const EmptyState = ({ colSpan }) => (
  <tr>
    <td colSpan={colSpan} style={{ padding: "48px 20px", textAlign: "center" }}>
      <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 12 }}>
        <div
          style={{
            width: 56,
            height: 56,
            borderRadius: 16,
            background: "var(--icon-btn-bg)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <RiSearchLine style={{ fontSize: 24, color: "var(--text-muted)" }} />
        </div>
        <p style={{ color: "var(--text-muted)", margin: 0, fontSize: "0.875rem" }}>
          {notFound.dataNotFound}
        </p>
      </div>
    </td>
  </tr>
);

const TableContainer = ({
  columns,
  data,
  isGlobalFilter,
  customPageSize,
  theadClass,
  trClass,
  thClass,
  shouldLineBreak,
  divClass,
  SearchPlaceholder,
  currentPage,
  setCurrentPage,
  totalPages,
  totalRecords,
  fetchData,
  fetchSortingData,
  setCustomPageSize,
  onSearch,
  manualPagination,
  manualFiltering,
}) => {
  const [columnFilters, setColumnFilters] = useState([]);
  const [globalFilter, setGlobalFilter] = useState("");
  const [pageSize, setPageSize] = useState(customPageSize);
  const [onValueSearch, setOnValueSearch] = useState("");
  const [sorting, setSorting] = useState([]);
  const [order, setOrder] = useState({});

  const fuzzyFilter = (row, columnId, value, addMeta) => {
    const itemRank = rankItem(row.getValue(columnId), value);
    addMeta({ itemRank });
    return itemRank.passed;
  };

  const table = useReactTable({
    columns,
    data: data || [],
    filterFns: { fuzzy: fuzzyFilter },
    state: { columnFilters, globalFilter, sorting },
    onColumnFiltersChange: setColumnFilters,
    onGlobalFilterChange: setGlobalFilter,
    onSortingChange: setSorting,
    globalFilterFn: fuzzyFilter,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
  });

  const {
    getHeaderGroups,
    getRowModel,
    getCanPreviousPage,
    getCanNextPage,
    nextPage,
    previousPage,
    getState,
  } = table;

  useEffect(() => {
    setPageSize(customPageSize);
    setCustomPageSize(customPageSize);
  }, [customPageSize, setCustomPageSize]);

  useEffect(() => {
    table.setPageSize(customPageSize);
  }, [totalRecords, customPageSize, table]);

  const handleSorting = (column) => {
    const colName = column.accessorKey;
    const isSorted = sorting?.find((s) => s.accessorKey === colName);
    const newSort = isSorted
      ? { accessorKey: colName, desc: !isSorted.desc }
      : { accessorKey: colName, desc: false };
    setSorting([newSort]);
    const dir = newSort.desc ? "DESC" : "ASC";
    setOrder({ [colName]: dir });
    fetchSortingData(currentPage, colName, dir);
  };

  const handleSearchInputChange = useCallback(
    (v) => {
      setOnValueSearch(v);
      onSearch?.(v);
    },
    [onSearch],
  );

  const handlePageSizeChange = (e) => {
    const val = e.target.value;
    setCurrentPage(1);
    if (val !== All) {
      setPageSize(Number(val));
      setCustomPageSize(Number(val));
      table.setPageSize(Number(val));
    } else {
      setPageSize(All);
      setCustomPageSize(totalRecords);
      table.setPageSize(totalRecords);
    }
  };

  const handlePageClick = (n) => {
    setCurrentPage(n);
    fetchData(n);
  };

  const renderPageNumbers = () => {
    const MAX = 5;
    let start = Math.max(1, currentPage - 2);
    let end = Math.min(totalPages, start + MAX - 1);
    if (end - start + 1 < MAX) start = Math.max(1, end - MAX + 1);
    const items = [];
    if (start > 1)
      items.push(
        <li key="s" className="page-item disabled">
          <span className="page-link">…</span>
        </li>,
      );
    for (let i = start; i <= end; i++) {
      items.push(
        <li key={i} className={`page-item${currentPage === i ? " active" : ""}`}>
          <Link
            className="page-link"
            onClick={(e) => {
              e.preventDefault();
              handlePageClick(i);
            }}
          >
            {i}
          </Link>
        </li>,
      );
    }
    if (end < totalPages)
      items.push(
        <li key="e" className="page-item disabled">
          <span className="page-link">…</span>
        </li>,
      );
    return items;
  };

  const startIndex = pageSize === All ? 1 : (currentPage - 1) * pageSize + 1;
  const endIndex = pageSize === All ? totalRecords : Math.min(currentPage * pageSize, totalRecords);

  return (
    <>
      {isGlobalFilter && (
        <div
          style={{
            display: "flex",
            flexWrap: "wrap",
            gap: 12,
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: 16,
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 8,
              fontSize: "0.8125rem",
              color: "var(--text-secondary)",
            }}
          >
            <span>Rows per page:</span>
            <select
              value={pageSize === totalRecords ? All : pageSize}
              onChange={handlePageSizeChange}
              className="form-select"
              style={{ width: "auto", padding: "5px 28px 5px 10px" }}
            >
              {[5, 10, 25, 50, 100].map((n) => (
                <option key={n} value={n}>
                  {n}
                </option>
              ))}
              <option value={All}>{All}</option>
            </select>
          </div>
          <div style={{ minWidth: 220 }}>
            {manualFiltering ? (
              <DebouncedInput
                value={onValueSearch ?? ""}
                onChange={handleSearchInputChange}
                placeholder={SearchPlaceholder || Search}
              />
            ) : (
              <DebouncedInput
                value={globalFilter ?? ""}
                onChange={setGlobalFilter}
                placeholder={SearchPlaceholder || Search}
              />
            )}
          </div>
        </div>
      )}

      <div className={divClass || "table-responsive"}>
        <table className={`table ${theadClass ? "" : ""}`}>
          <thead>
            {getHeaderGroups()?.map((hg) => (
              <tr className={trClass} key={hg.id}>
                {hg.headers?.map((header) => (
                  <th
                    key={header.id}
                    className={`${thClass || ""} text-center`}
                    style={{
                      cursor:
                        header.column.columnDef.enableSorting === undefined ? "pointer" : "default",
                    }}
                    onClick={() => {
                      if (header.column.columnDef.enableSorting === undefined)
                        handleSorting(header.column.columnDef);
                    }}
                  >
                    {header.isPlaceholder ? null : (
                      <span style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>
                        {flexRender(header.column.columnDef.header, header.getContext())}
                        {order[header.column.columnDef.accessorKey] === "ASC" && <RiArrowUpSLine />}
                        {order[header.column.columnDef.accessorKey] === "DESC" && (
                          <RiArrowDownSLine />
                        )}
                      </span>
                    )}
                  </th>
                ))}
              </tr>
            ))}
          </thead>
          <tbody className="text-center">
            {data && data.length > 0 ? (
              getRowModel()?.rows?.length > 0 ? (
                getRowModel().rows.map((row) => (
                  <tr key={row.id}>
                    {row.getVisibleCells().map((cell) => {
                      const val = cell.getValue();
                      return (
                        <td key={cell.id} className="text-center">
                          {val === null || val === "" ? (
                            "—"
                          ) : (
                            <div
                              className={shouldLineBreak === undefined ? "ellipsis" : undefined}
                              title={val}
                            >
                              {flexRender(cell.column.columnDef.cell, cell.getContext())}
                            </div>
                          )}
                        </td>
                      );
                    })}
                  </tr>
                ))
              ) : (
                <EmptyState colSpan={columns?.length} />
              )
            ) : (
              <EmptyState colSpan={columns?.length} />
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {data?.length !== 0 && (
        <div
          style={{
            display: "flex",
            flexWrap: "wrap",
            justifyContent: "space-between",
            alignItems: "center",
            gap: 12,
            marginTop: 16,
            paddingBottom: 8,
          }}
        >
          <span style={{ fontSize: "0.8125rem", color: "var(--text-muted)" }}>
            {manualPagination ? (
              <>
                Showing{" "}
                <strong style={{ color: "var(--text-primary)" }}>
                  {startIndex}–{endIndex}
                </strong>{" "}
                of <strong style={{ color: "var(--text-primary)" }}>{totalRecords}</strong>
              </>
            ) : (
              <>
                Showing{" "}
                <strong style={{ color: "var(--text-primary)" }}>
                  {getState().pagination.pageSize}
                </strong>{" "}
                of <strong style={{ color: "var(--text-primary)" }}>{data?.length}</strong>
              </>
            )}
          </span>
          <ul className="pagination mb-0">
            <li
              className={`page-item${manualPagination ? (currentPage === 1 ? " disabled" : "") : !getCanPreviousPage() ? " disabled" : ""}`}
            >
              <Link
                className="page-link"
                onClick={(e) => {
                  e.preventDefault();
                  manualPagination ? fetchData(currentPage - 1) : previousPage();
                }}
                aria-label="Previous"
              >
                <i className="fa fa-chevron-left" />
              </Link>
            </li>
            {manualPagination ? renderPageNumbers() : null}
            <li
              className={`page-item${manualPagination ? (currentPage === totalPages ? " disabled" : "") : !getCanNextPage() ? " disabled" : ""}`}
            >
              <Link
                className="page-link"
                onClick={(e) => {
                  e.preventDefault();
                  manualPagination ? fetchData(currentPage + 1) : nextPage();
                }}
                aria-label="Next"
              >
                <i className="fa fa-chevron-right" />
              </Link>
            </li>
          </ul>
        </div>
      )}
    </>
  );
};

export default TableContainer;
